import {useEffect, useRef, useState} from 'react';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {laDateEtHeure} from '../utils/dates';
import {FeedbackMessage, normalizeImages} from '../types/feedback';
import {editMessage, markUserRepliesAsRead, sendAdminMessage, subscribeToMessages} from '../services/feedbackService';
import {suggestFeedbackReply} from '../services/replySuggestionService';
import {ImageUploadError} from '../services/imageUpload';
import {FormattingToolbar} from './FormattingToolbar';
import {MarkdownText} from './MarkdownText';
import {
  AttachImagesButton,
  ImageSlot,
  imagesToSlots,
  MessageHeroImage,
  MessageImagesDisplay,
  MessageImagesEditor,
  resolveSlots,
  revokeSlotPreviews,
  useImageSlots,
} from './MessageImages';

const PRIMARY_COLOR = '#3ab3ff';

/**
 * Un message parti tout seul : le diagnostic d'achat, aujourd'hui.
 *
 * Il porte le texte, lisible d'un coup d'œil, et garde le HTML derrière un
 * repli — c'est ce que la personne a réellement reçu, images et boutons
 * compris, et c'est parfois la seule façon de comprendre ce à quoi elle
 * répond.
 *
 * Le HTML est affiché dans une `iframe` en bac à sable : il vient de notre
 * gabarit, mais il contient du texte que la personne nous a écrit, et rien
 * de ce qui s'affiche ici ne doit pouvoir agir sur cette page.
 */
function SystemMessage({message, sentAt}: { message: FeedbackMessage; sentAt: Date }) {
    const t = useTraduire();
  const [showHtml, setShowHtml] = useState(false);

  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] w-full">
        <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-4 py-3">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-medium text-teal-800">{message.authorName}</span>
            <span className="text-xs text-teal-600">{laDateEtHeure(sentAt)}</span>
            <span className="text-[10px] uppercase tracking-wide text-teal-700 bg-teal-100 rounded px-1.5 py-0.5">
              envoyé automatiquement
            </span>
          </div>
          {message.subject && (
            <p className="text-sm text-teal-900 mb-2">{message.subject}</p>
          )}
          {message.content && (
            <p className="whitespace-pre-wrap text-gray-900 text-sm">{message.content}</p>
          )}
          {message.html && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowHtml((open) => !open)}
                className="text-xs text-teal-700 underline"
              >
                {showHtml ? "Masquer l'e-mail" : "Voir l'e-mail tel qu'il a été reçu"}
              </button>
              {showHtml && (
                <iframe
                  title={t(T.messageEnvoye)}
                  sandbox=""
                  srcDoc={message.html}
                  className="mt-2 w-full rounded border border-teal-200 bg-white"
                  style={{height: 700}}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EditMessageButton({feedbackId, message}: {
  feedbackId: string;
  message: FeedbackMessage;
}) {
  const t = useTraduire();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(message.content);
  const [slots, setSlots] = useState<ImageSlot[]>([]);
  const [saving, setSaving] = useState(false);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  const startEditing = () => {
    setText(message.content);
    setSlots(imagesToSlots(normalizeImages(message.images, message.imageUrl)));
    setEditing(true);
  };

  const cancelEditing = () => {
    revokeSlotPreviews(slots);
    setEditing(false);
    setText(message.content);
    setSlots([]);
  };

  const handleSave = async () => {
    if (!text.trim() && slots.length === 0) return;
    setSaving(true);
    try {
      const images = await resolveSlots(slots, `conversation_images/${feedbackId}`);
      await editMessage(feedbackId, message.id, text.trim(), images);
      revokeSlotPreviews(slots);
      setEditing(false);
      setSlots([]);
    } catch (err) {
      console.error('Edit failed:', err);
      if (err instanceof ImageUploadError) alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={startEditing}
        className="text-[10px] text-white/50 hover:text-white/80 mt-1 transition-colors"
      >
        Edit
      </button>
    );
  }

  return (
    <div className="mt-2 flex gap-1.5">
      <div className="flex-1 flex flex-col gap-2">
        <FormattingToolbar
          textareaRef={editTextareaRef}
          getValue={() => text}
          onTextChange={setText}
        />
        <textarea
          ref={editTextareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
          className="w-full px-2 py-1 text-xs text-gray-900 border border-white/30 rounded resize-y focus:outline-none"
        />
        <MessageImagesEditor slots={slots} onChange={setSlots} disabled={saving}/>
        <div>
          <AttachImagesButton
            slots={slots}
            onChange={setSlots}
            disabled={saving}
            onError={(err) => err instanceof ImageUploadError && alert(err.message)}
            buttonClassName="text-white/60 hover:text-white transition-colors disabled:opacity-50"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="text-green-300 hover:text-green-100 disabled:opacity-50"
          title={t(T.enregistrer)}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
          </svg>
        </button>
        <button
          onClick={cancelEditing}
          className="text-red-300 hover:text-red-100"
          title={t(T.annuler)}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

interface ConversationSectionProps {
  feedbackId: string;
  originalMessage: string;
  originalCreatedAt: Date;
  adminEmail: string;
  adminName?: string;
}

export function ConversationSection({
  feedbackId,
  originalMessage,
  originalCreatedAt,
  adminEmail,
  adminName,
}: ConversationSectionProps) {
  const t = useTraduire();
  const [messages, setMessages] = useState<FeedbackMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSlots, setPendingSlots] = useImageSlots([]);
  const [uploading, setUploading] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Subscribe to messages
    const unsubscribe = subscribeToMessages(feedbackId, (msgs) => {
      setMessages(msgs);
    });

    // Mark user replies as read when opening
    markUserRepliesAsRead(feedbackId).catch(console.error);

    return () => unsubscribe();
  }, [feedbackId]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() && pendingSlots.length === 0) return;

    setSending(true);
    setError(null);

    try {
      let images: import('../types/feedback').ImageAttachment[] | undefined;
      if (pendingSlots.length > 0) {
        setUploading(true);
        images = await resolveSlots(pendingSlots, `conversation_images/${feedbackId}`);
        setUploading(false);
      }
      await sendAdminMessage(feedbackId, newMessage.trim(), adminEmail, adminName, images);
      setNewMessage('');
      revokeSlotPreviews(pendingSlots);
      setPendingSlots([]);
    } catch (err: unknown) {
      setUploading(false);
      console.error('Error sending message:', err);
      if (err instanceof ImageUploadError) {
        setError(err.message);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        const firebaseError = err as { code?: string };
        if (firebaseError.code === 'permission-denied') {
          setError('Permission denied. Make sure Firestore rules are deployed and you have admin claim.');
        } else {
          setError(`Failed to send message: ${errorMessage}`);
        }
      }
    } finally {
      setSending(false);
    }
  };

    // Suggestion IA : remplit le champ de réponse, à relire et corriger avant envoi. Rien n'est
    // envoyé à la personne — c'est un brouillon. Les connaissances produit viennent du BO.
    const handleSuggest = async () => {
        setSuggesting(true);
        setError(null);
        try {
            const suggestion = await suggestFeedbackReply(feedbackId);
            setNewMessage(suggestion);
            textareaRef.current?.focus();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Erreur inconnue';
            setError(`Impossible de générer une suggestion : ${message}`);
        } finally {
            setSuggesting(false);
        }
    };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter or Cmd+Enter to send
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{t(T.conversation)}</h3>

      {/* Messages container */}
      <div className="space-y-4 max-h-96 overflow-y-auto mb-4 p-4 bg-gray-50 rounded-lg">
        {/* Original feedback message */}
        <div className="flex justify-start">
          <div className="max-w-[80%]">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-500">{t(T.messageDorigine)}</span>
                <span className="text-xs text-gray-400">
                  {laDateEtHeure(originalCreatedAt)}
                </span>
              </div>
              <p className="text-gray-900 whitespace-pre-wrap">{originalMessage}</p>
            </div>
          </div>
        </div>

        {/* Conversation messages */}
        {messages.map((msg) => {
          // Le message automatique se range du côté de l'équipe — il part de
          // chez nous — mais ne prend pas sa couleur : personne ne l'a écrit,
          // et le confondre avec une réponse rédigée ferait croire qu'un
          // collègue s'est déjà penché sur le dossier.
          const isSystem = msg.authorType === 'system';
          const isAdmin = msg.authorType === 'admin' || isSystem;
          const createdAt = msg.createdAt?.toDate ? msg.createdAt.toDate() : new Date();

          if (isSystem) {
            return <SystemMessage key={msg.id} message={msg} sentAt={createdAt}/>;
          }

          return (
            <div
              key={msg.id}
              className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
            >
              <div className="max-w-[80%]">
                <div
                  className={`rounded-lg px-4 py-3 ${
                    isAdmin
                      ? 'text-white'
                      : 'bg-white border border-gray-200 shadow-sm'
                  }`}
                  style={isAdmin ? {backgroundColor: PRIMARY_COLOR} : undefined}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-xs font-medium ${
                        isAdmin ? 'text-white/70' : 'text-gray-500'
                      }`}
                    >
                      {msg.authorName}
                    </span>
                    <span
                      className={`text-xs ${isAdmin ? 'text-white/60' : 'text-gray-400'}`}
                    >
                      {laDateEtHeure(createdAt)}
                    </span>
                    {msg.editedAt && (
                      <span className={`text-[9px] italic ${isAdmin ? 'text-white/50' : 'text-gray-400'}`}>
                        (edited)
                      </span>
                    )}
                  </div>
                  <MessageHeroImage
                    images={msg.images}
                    legacyImageUrl={msg.imageUrl}
                    borderClass={isAdmin ? 'border border-white/20' : 'border border-gray-200'}
                  />
                  {msg.content && (
                    <p className={`whitespace-pre-wrap ${isAdmin ? 'text-white' : 'text-gray-900'}`}>
                      <MarkdownText
                        text={msg.content}
                        linkClassName={isAdmin ? 'text-white underline font-medium' : 'text-primary underline'}
                      />
                    </p>
                  )}
                  <MessageImagesDisplay
                    images={msg.images}
                    legacyImageUrl={msg.imageUrl}
                    thumbnailBorderClass={isAdmin ? 'border border-white/20' : 'border border-gray-200'}
                  />
                  {/* Edit button — admin can edit admin messages */}
                  {isAdmin && (
                    <EditMessageButton
                      feedbackId={feedbackId}
                      message={msg}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Image previews */}
      {pendingSlots.length > 0 && (
        <div className="mb-3">
          <MessageImagesEditor slots={pendingSlots} onChange={setPendingSlots} disabled={sending}/>
        </div>
      )}

        {/* Suggestion IA : brouillon à relire, jamais envoyé automatiquement */}
        <div className="mb-2">
            <button
                onClick={handleSuggest}
                disabled={suggesting || sending}
                className="px-3 py-1.5 rounded-pill text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-colors"
                style={{backgroundColor: PRIMARY_COLOR}}
                title="Rédige un brouillon de réponse à partir des connaissances produit du back-office. À relire avant envoi."
            >
                {suggesting ? 'Génération en cours…' : '✨ Suggérer une réponse'}
            </button>
        </div>

      {/* Reply input */}
      <div className="flex gap-3">
        <div className="flex-1 flex flex-col gap-2">
          <FormattingToolbar
            textareaRef={textareaRef}
            getValue={() => newMessage}
            onTextChange={setNewMessage}
          />
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t(T.ecrireUneReponse)}
            rows={3}
            disabled={sending}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:bg-gray-100 disabled:cursor-not-allowed resize-y"
            style={{'--tw-ring-color': PRIMARY_COLOR} as React.CSSProperties}
          />
        </div>
        <div className="flex flex-col gap-2">
          <AttachImagesButton
            slots={pendingSlots}
            onChange={setPendingSlots}
            disabled={sending}
            onError={(err) => err instanceof ImageUploadError && setError(err.message)}
            buttonClassName="px-3 py-2 h-fit border border-gray-300 rounded-pill text-sm text-gray-500 hover:bg-gray-50 hover:text-primary transition-colors disabled:opacity-50"
            iconClassName="w-5 h-5"
          />
          <button
            onClick={handleSend}
            disabled={sending || (!newMessage.trim() && pendingSlots.length === 0)}
            className="px-4 py-2 h-fit border border-transparent rounded-pill shadow-bo text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-colors"
            style={{backgroundColor: PRIMARY_COLOR}}
          >
            {uploading ? t(T.televersement) : sending ? t(T.envoiEnCoursPoints) : t(T.envoyer)}
          </button>
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-500">
        {t(T.avertissementNotification)}
      </p>

    </div>
  );
}
